import { useState, useEffect } from 'react'
import { DashboardLayout } from '@/components/layout/DashboardLayout'
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { SocialEngagementSettings } from "@/components/admin/SocialEngagementSettings";
import { AdminSocialCosts } from "@/components/admin/AdminSocialCosts";
import { AdminObligationsDashboard } from "@/components/admin/AdminObligationsDashboard";
import { AdminSocialActions } from "@/components/admin/AdminSocialActions";
import { TreeVisualization } from "@/components/customer/TreeVisualization";
import { CityVisualization } from "@/components/customer/CityVisualization";
import { supabase } from '@/integrations/supabase/client'
import { useAuth } from '@/hooks/useAuth'
import { useToast } from '@/hooks/use-toast'
import { Trees, Heart, Euro, CheckSquare, Activity, TrendingUp, Users } from "lucide-react";

interface AdminProjectSummary {
  project_id: string;
  project_name: string;
  project_type: string;
  total_contributions: number;
  customer_count: number;
}

interface CustomerContribution {
  customer_id: string;
  customer_name: string;
  customer_email: string;
  contributions: {
    project_id: string;
    project_name: string;
    project_type: string;
    contribution_count: number;
  }[];
}

interface SocialProject {
  id: string;
  name: string;
  project_type: string;
}

const AdminSocialEngagement = () => {
  const { user } = useAuth()
  const { toast } = useToast()
  const [projectSummaries, setProjectSummaries] = useState<AdminProjectSummary[]>([]);
  const [customerContributions, setCustomerContributions] = useState<CustomerContribution[]>([]);
  const [availableProjects, setAvailableProjects] = useState<SocialProject[]>([]);
  const [tenantId, setTenantId] = useState<string>('');
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    if (user?.id) {
      fetchAdminData()
    }
  }, [user?.id])

  const fetchAdminData = async () => {
    if (!user?.id) return

    try {
      // Get tenant for current admin
      const { data: tenant, error: tenantError } = await supabase
        .from('tenants')
        .select('id, name')
        .eq('admin_user_id', user.id)
        .single();

      if (tenantError) throw tenantError;

      setTenantId(tenant.id);

      // Fetch all available social projects
      const { data: allProjects, error: allProjectsError } = await supabase
        .from('social_projects')
        .select('id, name, project_type')
        .eq('is_active', true);

      if (allProjectsError) throw allProjectsError;

      // Fetch enabled social projects for this tenant
      const { data: tenantProjects, error: tenantProjectsError } = await supabase
        .from('tenant_social_projects')
        .select(`
          social_project_id,
          social_projects!inner (
            name,
            project_type
          )
        `)
        .eq('tenant_id', tenant.id)
        .eq('is_enabled', true);

      if (tenantProjectsError) throw tenantProjectsError;

      // Get summaries for each project
      const summaryData: AdminProjectSummary[] = [];
      
      for (const tenantProject of tenantProjects || []) {
        const { data: contributions, error: contributionsError } = await supabase
          .from('customer_social_contributions')
          .select(`
            contribution_count,
            customers!inner (
              id,
              tenant_id
            )
          `)
          .eq('social_project_id', tenantProject.social_project_id)
          .eq('customers.tenant_id', tenant.id);

        if (contributionsError) {
          console.error('Error fetching contributions:', contributionsError);
          continue;
        }

        const totalContributions = contributions?.reduce((sum, c) => sum + c.contribution_count, 0) || 0;
        const customerCount = contributions?.length || 0;

        summaryData.push({
          project_id: tenantProject.social_project_id,
          project_name: (tenantProject.social_projects as any).name,
          project_type: (tenantProject.social_projects as any).project_type,
          total_contributions: totalContributions,
          customer_count: customerCount
        });
      }

      // Get detailed customer contributions
      const { data: detailedContributions, error: detailedError } = await supabase
        .from('customer_social_contributions')
        .select(`
          customer_id,
          contribution_count,
          social_project_id,
          customers!inner (
            full_name,
            email,
            tenant_id
          ),
          social_projects (
            name,
            project_type
          )
        `)
        .eq('customers.tenant_id', tenant.id)
        .gt('contribution_count', 0);

      if (detailedError) throw detailedError;

      // Group by customer
      const customerMap = new Map<string, CustomerContribution>();
      
      detailedContributions?.forEach((contrib: any) => {
        const customerId = contrib.customer_id;
        
        if (!customerMap.has(customerId)) {
          customerMap.set(customerId, {
            customer_id: customerId,
            customer_name: contrib.customers.full_name,
            customer_email: contrib.customers.email,
            contributions: []
          });
        }
        
        customerMap.get(customerId)!.contributions.push({
          project_id: contrib.social_project_id,
          project_name: contrib.social_projects.name,
          project_type: contrib.social_projects.project_type,
          contribution_count: contrib.contribution_count
        });
      });

      const contributionData = Array.from(customerMap.values());

      setAvailableProjects(allProjects || []);
      setProjectSummaries(summaryData);
      setCustomerContributions(contributionData);
    } catch (error) {
      console.error('Error fetching admin data:', error)
      toast({
        variant: 'destructive',
        title: 'Fehler',
        description: 'Daten konnten nicht geladen werden.'
      })
    } finally {
      setLoading(false)
    }
  }

  const getProjectIcon = (type: string) => {
    switch (type) {
      case 'trees': return <Trees className="h-5 w-5 text-green-600" />
      case 'family_help': return <Heart className="h-5 w-5 text-red-600" />
      default: return <Heart className="h-5 w-5 text-primary" />
    }
  }

  return (
    <DashboardLayout>
      {loading ? (
        <div className="flex items-center justify-center h-64">
          <div className="text-center">Lade Daten...</div>
        </div>
      ) : (
        <div className="space-y-6">
          <div className="flex items-center justify-between">
            <h1 className="text-3xl font-bold">Soziales Engagement</h1>
          </div>
          
          <Tabs defaultValue="overview" className="space-y-6">
            <TabsList className="grid w-full grid-cols-4">
              <TabsTrigger value="overview" className="flex items-center gap-2">
                <TrendingUp className="h-4 w-4" />
                Übersicht
              </TabsTrigger>
              <TabsTrigger value="costs" className="flex items-center gap-2">
                <Euro className="h-4 w-4" />
                Kosten & Einstellungen
              </TabsTrigger>
              <TabsTrigger value="obligations" className="flex items-center gap-2">
                <CheckSquare className="h-4 w-4" />
                Meine Verpflichtungen
              </TabsTrigger>
              <TabsTrigger value="actions" className="flex items-center gap-2">
                <Activity className="h-4 w-4" />
                Aktions-Protokoll
              </TabsTrigger>
            </TabsList>

            <TabsContent value="overview" className="space-y-6">
              <SocialEngagementSettings />
              
              {/* Visual Overview Section */}
              <div className="space-y-6">
                <Card>
                  <CardHeader>
                    <CardTitle className="flex items-center gap-2">
                      <TrendingUp className="h-5 w-5" />
                      Visuelle Übersicht
                    </CardTitle>
                    <CardDescription>
                      Visualisierung der sozialen Projekte und Beiträge Ihrer Kunden
                    </CardDescription>
                  </CardHeader>
                  <CardContent>
                    <div className="grid grid-cols-1 lg:grid-cols-2 gap-8">
                      {projectSummaries.map((project) => (
                        <div key={project.project_id} className="space-y-4">
                          {project.project_type === 'trees' && (
                            <div className="text-center">
                              <h3 className="text-lg font-semibold mb-4 flex items-center justify-center gap-2">
                                <Trees className="h-5 w-5 text-green-600" />
                                {project.project_name}
                              </h3>
                              <TreeVisualization 
                                totalTrees={project.total_contributions} 
                                userTrees={0} 
                                projectName={project.project_name}
                              />
                            </div>
                          )}
                          
                          {project.project_type === 'family_help' && (
                            <div className="text-center">
                              <h3 className="text-lg font-semibold mb-4 flex items-center justify-center gap-2">
                                <Heart className="h-5 w-5 text-red-600" />
                                {project.project_name}
                              </h3>
                              <CityVisualization 
                                totalHouses={project.total_contributions} 
                                userHouses={0}
                                projectName={project.project_name}
                              />
                            </div>
                          )}
                        </div>
                      ))}
                    </div>
                  </CardContent>
                </Card>

                {/* Statistics Cards */}
                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
                  {projectSummaries.map((project) => (
                    <Card key={`stats-${project.project_id}`}>
                      <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
                        <CardTitle className="text-sm font-medium">
                          {project.project_name}
                        </CardTitle>
                        {getProjectIcon(project.project_type)}
                      </CardHeader>
                      <CardContent>
                        <div className="text-2xl font-bold">{project.total_contributions}</div>
                        <p className="text-xs text-muted-foreground">
                          {project.customer_count} aktive Kunden
                        </p>
                      </CardContent>
                    </Card>
                  ))}
                </div>

                {/* Detailed Customer Contributions */}
                <Card>
                  <CardHeader>
                    <CardTitle className="flex items-center gap-2">
                      <Users className="h-5 w-5" />
                      Detaillierte Kundenbeiträge
                    </CardTitle>
                    <CardDescription>
                      Übersicht über die Beiträge einzelner Kunden zu sozialen Projekten
                    </CardDescription>
                  </CardHeader>
                  <CardContent>
                    <div className="space-y-4">
                      {customerContributions.length === 0 ? (
                        <p className="text-muted-foreground text-center py-8">
                          Noch keine Kundenbeiträge vorhanden.
                        </p>
                      ) : (
                        customerContributions.map((customer) => (
                          <div key={customer.customer_id} className="border rounded-lg p-4">
                            <div className="flex items-start justify-between mb-3">
                              <div>
                                <h4 className="font-medium">{customer.customer_name}</h4>
                                <p className="text-sm text-muted-foreground">{customer.customer_email}</p>
                              </div>
                            </div>
                            
                            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-3">
                              {customer.contributions.map((contribution) => (
                                <div
                                  key={contribution.project_id}
                                  className="flex items-center justify-between p-2 bg-secondary rounded"
                                >
                                  <div className="flex items-center gap-2">
                                    {getProjectIcon(contribution.project_type)}
                                    <span className="text-sm font-medium">
                                      {contribution.project_name}
                                    </span>
                                  </div>
                                  <span className="text-sm font-bold">
                                    {contribution.contribution_count}
                                  </span>
                                </div>
                              ))}
                            </div>
                          </div>
                        ))
                      )}
                    </div>
                  </CardContent>
                </Card>
              </div>
            </TabsContent>

            <TabsContent value="costs" className="space-y-6">
              <AdminSocialCosts tenantId={tenantId} availableProjects={availableProjects} />
            </TabsContent>

            <TabsContent value="obligations" className="space-y-6">
              <AdminObligationsDashboard tenantId={tenantId} />
            </TabsContent>

            <TabsContent value="actions" className="space-y-6">
              <AdminSocialActions tenantId={tenantId} availableProjects={availableProjects} />
            </TabsContent>
          </Tabs>
        </div>
      )}
    </DashboardLayout>
  );
};

export default AdminSocialEngagement;